import React, { useCallback } from "react";
import { Grid, Typography } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import Modal from "@/components/Modal";
import Button from "@/components/Button";
import GridIconText from "@/components/GridIconText";
import GetImages from "@/components/GetImages";
import Dialog from "@/components/Dialog";

import style from "./index.module.css";

import { transformClasses } from "@/utils";
import { styles } from "@/utils/styles";
import useIsMobile from "@/utils/hooks/useIsMobile";
import { locationAccessDetail } from "@/utils/common";
import useIsTablet from "@/utils/hooks/useIsTablet";
import { useGeolocation } from "@/utils/hooks/useGeolocation";
import { storage } from "@/utils/Storage";
import { durgapurCoordinates } from "@/utils/Config";
import { useGooglePlaces } from "@/utils/hooks/useGooglePlaces";

import { IButtonVariant } from "@/models/button";
import { ISessionKeys } from "@/models";

interface IProps {
  isOpen: boolean;
  closeHandler: () => void;
}
const LocationAccess: React.FC<IProps> = (props) => {
  const { isOpen, closeHandler } = props;

  const { getLocation, isLoading } = useGeolocation();
  const { getAddressFromCoordinates } = useGooglePlaces({
    apiKey: import.meta.env.VITE_APP_GOOGLE_PLACES_API_KEY,
  });

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const isMobileOrTablet = isMobile || isTablet;
  const navigate = useNavigate();

  const markDefaultLocationSkipHandler = () => {
    storage.setSessionToken(
      ISessionKeys.LOCATION,
      JSON.stringify({
        latitude: durgapurCoordinates.latitude,
        longitude: durgapurCoordinates.longitude,
      })
    );
    if (isMobileOrTablet) {
      getAddressFromCoordinates({
        lat: durgapurCoordinates.latitude,
        lng: durgapurCoordinates.longitude,
      });
    }
    navigate("/find-doctor", { state: { navItem: 1 } });
    closeHandler();
  };

  const locationAccessScreen = useCallback(() => {
    return (
      <>
        <Grid item className={style.locationHeader}>
          <Grid item>
            <Grid item className={style.header}>
              <GetImages name="LocationAccessIcon" width="36" height="36" />
              <Typography component={"h3"} className={style.heading}>
                <FormattedMessage
                  id="enableLocationAccess"
                  defaultMessage="Enable Location Access"
                />
              </Typography>
            </Grid>
            <Typography component={"p"} className={style.subHeading}>
              <FormattedMessage
                id="locationAccessSubHead"
                defaultMessage="To provide you with the best experience, we need access to your
              location. This helps us to:"
              />
            </Typography>
          </Grid>
          <Grid item className={style.locationGridContainer}>
            {locationAccessDetail.map((info, index) => (
              <GridIconText key={`${info.title}-${index}`} {...info} />
            ))}
          </Grid>
          <Grid item className={style.locationMiddleText}>
            <Typography
              component={"p"}
              className={transformClasses(
                style.locationAccessText,
                style.locationLabeling
              )}
            >
              <FormattedMessage
                id="privacyLocationText"
                defaultMessage="We respect your privacy. Your location data will be used only for
              these purposes and will not be shared with third parties."
              />
            </Typography>
          </Grid>
        </Grid>

        <Grid item className={style.buttonContainer}>
          <Typography
            component={"p"}
            className={transformClasses(
              style.locationAccessText,
              style.locationLabeling
            )}
            sx={styles.mb20}
          >
            <FormattedMessage
              id="allowAccessText"
              defaultMessage="By allowing access, you agree to our"
            />
            <Typography
              component={"span"}
              className={transformClasses(
                style.orangeText,
                style.locationLabeling
              )}
            >
              {" "}
              <FormattedMessage
                id="privacyPolicyText"
                defaultMessage="Privacy Policy"
              />
            </Typography>
          </Typography>
          <Grid item xs={12} className={style.btnContainer}>
            <Button
              isLoading={isLoading}
              btnTrigger={() => getLocation(closeHandler)}
            >
              <FormattedMessage
                id="allowLocationBtn"
                defaultMessage="Allow Location Access"
              />
            </Button>
            <Button
              variant={IButtonVariant.WHITE}
              btnTrigger={markDefaultLocationSkipHandler}
            >
              <FormattedMessage id="skip" defaultMessage="Skip" />
            </Button>
          </Grid>
        </Grid>
      </>
    );
  }, [isLoading]);

  return (
    <>
      {isMobileOrTablet ? (
        <Modal lineBottomBackground="white">{locationAccessScreen()}</Modal>
      ) : (
        <Dialog fullWidth maxWidth="xs" isOpen={isOpen}>
          {locationAccessScreen()}
        </Dialog>
      )}
    </>
  );
};

export default LocationAccess;
